import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { InteractiveParticles } from "../components/InteractiveParticles";
import {
  Logo,
  Button,
  Input,
  Form,
  FormField,
  FormItem,
  FormControl,
  Alert,
  AlertTitle,
  AlertDescription,
} from "@pentatonic-ltd/ui";
import { CheckCircle } from "@solar-icons/react";

export const Route = createFileRoute("/forgot-password")({
  component: ForgotPasswordPage,
});

const ASSETS = {
  kiehls: "/images/kiehls.png",
  bq: "/images/bq.png",
  hyundai: "/images/hyundai.png",
  starbucks: "/images/starbucks.png",
};

function RightPanel() {
  return (
    <div className="hidden lg:flex flex-1 bg-[#171717] relative overflow-hidden">
      <InteractiveParticles className="absolute inset-0" />
      <div className="absolute bottom-12 left-0 right-0 flex flex-col gap-6 items-center pointer-events-none z-10">
        <p className="font-mono font-extralight text-[14px] uppercase tracking-[0.28px] text-white/60 text-center">
          Trusted by the world's leading consumer brands
        </p>
        <div className="flex gap-20 items-center">
          <div className="h-[37px] w-[74px] shrink-0">
            <img src={ASSETS.kiehls} alt="Kiehl's" className="h-full w-full object-contain object-left" />
          </div>
          <div className="h-[35px] w-[35px] shrink-0">
            <img src={ASSETS.bq} alt="B&Q" className="h-full w-full object-contain" />
          </div>
          <div className="h-[16px] w-[125px] shrink-0">
            <img src={ASSETS.hyundai} alt="Hyundai" className="h-full w-full object-contain" />
          </div>
          <div className="h-[37px] w-[37px] shrink-0">
            <img src={ASSETS.starbucks} alt="Starbucks" className="h-full w-full object-contain" />
          </div>
        </div>
      </div>
    </div>
  );
}

interface ForgotPasswordFormValues {
  email: string;
}

function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<ForgotPasswordFormValues>({
    defaultValues: { email: "" },
  });

  const onSubmit = async (values: ForgotPasswordFormValues) => {
    setError(null);
    try {
      const res = await fetch("/api/v1/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: values.email }),
      });
      const result = await res.json() as { success: boolean; error?: string; data?: { resetToken?: string } };

      if (!result.success) {
        setError("Something went wrong. Please try again.");
        return;
      }

      setResetToken(result.data?.resetToken ?? null);
      setSent(true);
    } catch {
      setError("Something went wrong. Please try again.");
    }
  };

  return (
    <div className="min-h-screen w-full flex bg-[#171717]">
      <div className="w-full lg:w-[560px] lg:shrink-0 bg-white dark:bg-[#171717] flex flex-col min-h-screen">
        <div className="flex flex-col flex-1 w-full max-w-[560px] mx-auto px-10 pt-12 pb-10">

          {!sent ? (
            <div className="flex flex-col gap-10">
              <a href="/"><Logo variant="auto" size="lg" /></a>

              <div className="flex flex-col gap-10">
                <div className="flex flex-col gap-2">
                  <h1 className="text-[29px] leading-[1.4] font-light text-[#171717] dark:text-white">
                    Reset your password
                  </h1>
                  <p className="text-[18px] leading-[1.4] font-light text-[#A9A9A9]">
                    Enter your work email and we'll send you a reset link.
                  </p>
                </div>

                <Form form={form} onSubmit={onSubmit} className="flex flex-col gap-6">
                  {error && (
                    <Alert variant="error">
                      <AlertTitle>Couldn't send link</AlertTitle>
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <FormField
                    control={form.control}
                    name="email"
                    rules={{ required: "Email is required" }}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            type="email"
                            label="Email address"
                            variant={fieldState.error ? "error" : "default"}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    variant="primary"
                    fullWidth
                    loading={form.formState.isSubmitting}
                  >
                    Send reset link
                  </Button>
                </Form>
              </div>

              <p className="mt-auto pt-10 text-[14px] text-[#A9A9A9]">
                <a href="/" className="text-[#171717] dark:text-white underline underline-offset-2">
                  ← Back to sign in
                </a>
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-10">
              <a href="/"><Logo variant="auto" size="lg" /></a>

              <div className="flex flex-col gap-6">
                <CheckCircle size={48} color="#171717" weight="LineDuotone" />
                <div className="flex flex-col gap-2">
                  <h1 className="text-[29px] leading-[1.4] font-light text-[#171717] dark:text-white">
                    Check your email
                  </h1>
                  <p className="text-[18px] leading-[1.4] font-light text-[#A9A9A9]">
                    If an account exists for{" "}
                    <span className="text-[#171717] dark:text-white">{form.getValues("email")}</span>
                    , you'll receive a reset link shortly.
                  </p>
                </div>

                {resetToken && (
                  <div className="p-3 border border-[#A9A9A9] bg-[#FAFAFA] font-mono font-extralight text-[14px] uppercase tracking-wider text-[#171717]">
                    Dev stub — reset link:{" "}
                    <a
                      href={`/reset-password?token=${resetToken}`}
                      className="underline"
                    >
                      /reset-password?token={resetToken}
                    </a>
                  </div>
                )}
              </div>

              <p className="mt-auto pt-10 text-[14px] text-[#A9A9A9]">
                <a href="/" className="text-[#171717] dark:text-white underline underline-offset-2">
                  ← Back to sign in
                </a>
              </p>
            </div>
          )}
        </div>
      </div>
      <RightPanel />
    </div>
  );
}
